import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Footprints,
  Loader2,
  RotateCw,
  Ticket,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const PRICE_MAP = { katvr: { 30: 19, 60: 32 }, chair360: { 20: 5 } };
const STEPS = ["Machine", "Expérience", "Créneau", "Joueur"];
const MACHINES = [
  {
    id: "katvr",
    label: "Tapis KAT VR Walk Mini",
    desc: "Marche & course omnidirectionnelle — jusqu'à 5 joueurs en simultané",
    icon: Footprints,
    accent: "cyan",
  },
  {
    id: "chair360",
    label: "Chaise VR 360°",
    desc: "Rotations gyroscopiques complètes — 1 joueur, sensations maximales",
    icon: RotateCw,
    accent: "magenta",
  },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const maxISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

const inputCls =
  "w-full border border-line bg-void px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-cyan-400";

export const BookingFlow = ({ preset }) => {
  const [step, setStep] = useState(1);
  const [machine, setMachine] = useState(null);
  const [experience, setExperience] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [slot, setSlot] = useState(null);
  const [duration, setDuration] = useState(null);
  const [players, setPlayers] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [experiences, setExperiences] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [paying, setPaying] = useState(false);

  const payOnline = async () => {
    if (!confirmation) return;
    setPaying(true);
    try {
      const lookup =
        confirmation.machine === "chair360"
          ? "chair_20"
          : confirmation.duration === 30
            ? "kat_30"
            : "kat_60";
      localStorage.setItem(
        "ip_last_booking",
        JSON.stringify({
          reference: confirmation.reference,
          experience: confirmation.experience,
          date: confirmation.date,
          time_slot: confirmation.time_slot,
          total_price: confirmation.total_price,
        })
      );
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        quantity: confirmation.players,
        origin_url: window.location.origin,
        booking_id: confirmation.id,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Impossible d'ouvrir le paiement"));
      setPaying(false);
    }
  };

  useEffect(() => {
    api
      .get("/experiences")
      .then((r) => setExperiences(r.data))
      .catch(() => setExperiences([]));
  }, []);

  useEffect(() => {
    if (!preset) return;
    if (preset.machine) {
      setMachine(preset.machine);
      setDuration(preset.duration || (preset.machine === "chair360" ? 20 : null));
      if (preset.machine === "chair360") setPlayers(1);
      setStep(2);
    }
  }, [preset]);

  useEffect(() => {
    if (preset?.experience && experiences.length) {
      const exp = experiences.find((e) => e.slug === preset.experience);
      if (exp) {
        setMachine(exp.machine);
        setExperience(exp);
        setDuration(preset.duration || exp.duration);
        if (exp.machine === "chair360") setPlayers(1);
        setStep(3);
      }
    }
  }, [preset, experiences]);

  useEffect(() => {
    if (!machine || !date) return;
    setSlot(null);
    setSlotsLoading(true);
    api
      .get("/availability", { params: { date, machine } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [machine, date]);

  const selectMachine = (id) => {
    setMachine(id);
    setExperience(null);
    setSlot(null);
    setDuration(id === "chair360" ? 20 : null);
    setPlayers(1);
    setStep(2);
  };

  const compatible = experiences.filter((e) => e.machine === machine);
  const durationOptions = machine === "katvr" ? [30, 60] : [20];
  const playerOptions = machine === "katvr" ? [1, 2, 3, 4, 5] : [1];
  const total = machine && duration ? PRICE_MAP[machine][duration] * players : 0;

  const stepValid = [
    !!machine,
    !!experience,
    !!(date && slot && duration),
    form.name.trim().length >= 2 &&
      /\S+@\S+\.\S+/.test(form.email) &&
      form.phone.trim().length >= 6,
  ][step - 1];

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        machine,
        experience: experience.title,
        date,
        time_slot: slot,
        duration,
        players,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      setConfirmation(data);
      toast.success("Réservation enregistrée !");
    } catch (e) {
      toast.error(formatApiError(e, "Impossible de créer la réservation"));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setConfirmation(null);
    setStep(1);
    setMachine(null);
    setExperience(null);
    setSlot(null);
    setDuration(null);
    setPlayers(1);
    setForm({ name: "", email: "", phone: "" });
  };

  return (
    <section id="reservation" data-testid="booking-section" className="relative bg-surface py-24 sm:py-28">
      <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-[120px]" />
      <div className="relative mx-auto max-w-4xl px-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <p className="font-hud text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"// 04 — Réservation"}
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Bloquez votre <span className="text-glow-cyan text-cyan-400">session</span>
          </h2>
        </motion.div>

        <div className="glass-panel mt-12 p-6 sm:p-10">
          {confirmation ? (
            <motion.div
              data-testid="booking-confirmation"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
              <h3 className="mt-5 font-display text-2xl font-bold uppercase text-slate-100">
                Session verrouillée
              </h3>
              <p className="mt-2 text-sm text-slate-400">
                Présentez ce code à l'accueil le jour J. Un email de confirmation
                vient d'être envoyé à {confirmation.email}.
              </p>
              <div className="mx-auto mt-7 inline-block border border-emerald-500/40 bg-emerald-500/10 px-8 py-4">
                <span data-testid="booking-reference" className="font-orbitron text-3xl font-black tracking-[0.15em] text-emerald-300">
                  {confirmation.reference}
                </span>
              </div>
              <div className="mx-auto mt-8 grid max-w-md gap-2 text-left font-hud text-xs uppercase tracking-wider text-slate-400">
                <div className="flex justify-between border-b border-line py-2">
                  <span>Expérience</span>
                  <span className="text-slate-200">{confirmation.experience}</span>
                </div>
                <div className="flex justify-between border-b border-line py-2">
                  <span>Machine</span>
                  <span className="text-slate-200">{confirmation.machine_label}</span>
                </div>
                <div className="flex justify-between border-b border-line py-2">
                  <span>Date</span>
                  <span className="text-slate-200">
                    {confirmation.date} · {confirmation.time_slot}
                  </span>
                </div>
                <div className="flex justify-between border-b border-line py-2">
                  <span>Joueurs</span>
                  <span className="text-slate-200">{confirmation.players}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span>Total</span>
                  <span className="text-cyan-300">{confirmation.total_price}€</span>
                </div>
              </div>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <button
                  data-testid="booking-pay-btn"
                  onClick={payOnline}
                  disabled={paying}
                  className="inline-flex items-center gap-2 bg-cyan-400 px-8 py-3.5 font-hud text-xs font-bold uppercase tracking-[0.2em] text-void transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_28px_rgba(0,240,255,0.5)] disabled:opacity-40"
                >
                  {paying ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  Payer en ligne — {confirmation.total_price}€
                </button>
                <button
                  data-testid="booking-onsite-btn"
                  onClick={() =>
                    toast.success(
                      `Paiement sur place choisi — vous réglerez ${confirmation.total_price}€ à l'accueil`
                    )
                  }
                  className="inline-flex items-center gap-2 border border-line px-8 py-3.5 font-hud text-xs font-bold uppercase tracking-[0.2em] text-slate-200 transition-all duration-300 hover:border-cyan-400/60 hover:text-cyan-300"
                >
                  <Wallet className="h-4 w-4" />
                  Payer sur place
                </button>
              </div>
              <button
                data-testid="booking-new-btn"
                onClick={reset}
                className="mt-9 border border-cyan-500/40 px-8 py-3 font-hud text-xs font-bold uppercase tracking-[0.2em] text-cyan-300 transition-all duration-300 hover:bg-cyan-400 hover:text-void"
              >
                Nouvelle réservation
              </button>
            </motion.div>
          ) : (
            <>
              <div data-testid="booking-steps" className="mb-10">
                <div className="flex justify-between">
                  {STEPS.map((label, i) => (
                    <div key={label} className="flex flex-col items-center gap-2">
                      <span
                        className={`font-orbitron text-sm font-bold ${
                          step > i ? "text-cyan-400" : "text-slate-600"
                        }`}
                      >
                        0{i + 1}
                      </span>
                      <span
                        className={`font-hud text-[9px] uppercase tracking-[0.2em] sm:text-[10px] ${
                          step > i ? "text-slate-300" : "text-slate-600"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 h-0.5 bg-line">
                  <motion.div
                    className="h-full bg-cyan-400 shadow-[0_0_12px_rgba(0,240,255,0.6)]"
                    animate={{ width: `${(step / 4) * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.35 }}
                >
                  {step === 1 && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      {MACHINES.map((m) => (
                        <button
                          key={m.id}
                          data-testid={`booking-machine-${m.id}`}
                          onClick={() => selectMachine(m.id)}
                          className={`group border p-6 text-left transition-all duration-300 ${
                            machine === m.id
                              ? m.accent === "cyan"
                                ? "border-cyan-400 bg-cyan-400/10"
                                : "border-fuchsia-500 bg-fuchsia-500/10"
                              : "border-line bg-void hover:border-cyan-500/40"
                          }`}
                        >
                          <m.icon
                            className={`h-8 w-8 ${m.accent === "cyan" ? "text-cyan-400" : "text-fuchsia-500"}`}
                          />
                          <h3 className="mt-4 font-display text-lg font-bold uppercase text-slate-100">
                            {m.label}
                          </h3>
                          <p className="mt-2 text-sm text-slate-400">{m.desc}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  {step === 2 && (
                    <div className="grid gap-3">
                      {compatible.map((exp) => (
                        <button
                          key={exp.slug}
                          data-testid={`booking-experience-${exp.slug}`}
                          onClick={() => {
                            setExperience(exp);
                            if (machine === "chair360") setDuration(20);
                            setStep(3);
                          }}
                          className={`flex items-center gap-4 border p-4 text-left transition-all duration-300 ${
                            experience?.slug === exp.slug
                              ? "border-cyan-400 bg-cyan-400/10"
                              : "border-line bg-void hover:border-cyan-500/40"
                          }`}
                        >
                          <img
                            src={exp.image}
                            alt={exp.title}
                            className="h-14 w-14 shrink-0 object-cover"
                          />
                          <div className="min-w-0 flex-1">
                            <h4 className="font-display text-base font-bold uppercase text-slate-100">
                              {exp.title}
                            </h4>
                            <p className="truncate text-xs text-slate-500">
                              {exp.category} · {exp.difficulty} · {exp.players} joueur(s)
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-8">
                      <div>
                        <label className="mb-3 block font-hud text-[10px] uppercase tracking-[0.25em] text-slate-500">
                          Date de la session
                        </label>
                        <input
                          data-testid="booking-date-input"
                          type="date"
                          value={date}
                          min={todayISO()}
                          max={maxISO()}
                          onChange={(e) => setDate(e.target.value)}
                          className={`${inputCls} [color-scheme:dark]`}
                        />
                      </div>
                      <div>
                        <label className="mb-3 block font-hud text-[10px] uppercase tracking-[0.25em] text-slate-500">
                          Durée
                        </label>
                        <div className="flex gap-3">
                          {durationOptions.map((d) => (
                            <button
                              key={d}
                              data-testid={`booking-duration-${d}`}
                              onClick={() => setDuration(d)}
                              className={`flex-1 border py-3 font-hud text-xs uppercase tracking-[0.2em] transition-all duration-300 ${
                                duration === d
                                  ? "border-cyan-400 bg-cyan-400/10 text-cyan-300"
                                  : "border-line text-slate-400 hover:border-cyan-500/40"
                              }`}
                            >
                              {d} min — {PRICE_MAP[machine][d]}€
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="mb-3 block font-hud text-[10px] uppercase tracking-[0.25em] text-slate-500">
                          Créneau horaire
                        </label>
                        {slotsLoading ? (
                          <div className="flex justify-center py-6">
                            <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
                          </div>
                        ) : slots.length === 0 ? (
                          <p
                            data-testid="booking-closed"
                            className="border border-line bg-void p-4 text-center font-hud text-xs uppercase tracking-[0.2em] text-slate-500"
                          >
                            Salle fermée ce jour-là — choisissez une autre date
                          </p>
                        ) : (
                          <div data-testid="booking-slots" className="grid grid-cols-4 gap-2">
                            {slots.map((s) => (
                              <button
                                key={s.time}
                                data-testid={`booking-slot-${s.time.replace(":", "")}`}
                                disabled={!s.available}
                                onClick={() => setSlot(s.time)}
                                className={`border py-2.5 font-hud text-xs transition-all duration-300 ${
                                  slot === s.time
                                    ? "border-cyan-400 bg-cyan-400/10 text-cyan-300"
                                    : s.available
                                      ? "border-line text-slate-300 hover:border-cyan-500/40"
                                      : "cursor-not-allowed border-line/50 text-slate-700 line-through"
                                }`}
                              >
                                {s.time}
                                {machine === "katvr" && s.available && s.remaining < 5 && (
                                  <span className="block text-[9px] text-amber-400">
                                    reste {s.remaining}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-6">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <input
                          data-testid="booking-name-input"
                          placeholder="Nom complet"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className={inputCls}
                        />
                        <input
                          data-testid="booking-phone-input"
                          placeholder="Téléphone"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className={inputCls}
                        />
                        <input
                          data-testid="booking-email-input"
                          type="email"
                          placeholder="Email"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className={`${inputCls} sm:col-span-2`}
                        />
                        <div>
                          <label className="mb-2 block font-hud text-[10px] uppercase tracking-[0.25em] text-slate-500">
                            Joueurs
                          </label>
                          <select
                            data-testid="booking-players-select"
                            value={players}
                            onChange={(e) => setPlayers(Number(e.target.value))}
                            className={inputCls}
                          >
                            {playerOptions.map((p) => (
                              <option key={p} value={p}>
                                {p} joueur{p > 1 ? "s" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-end justify-end">
                          <div className="text-right">
                            <span className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-500">
                              Total estimé
                            </span>
                            <div data-testid="booking-total" className="font-orbitron text-3xl font-black text-cyan-400">
                              {total}€
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="border border-line bg-void p-4 font-hud text-[11px] uppercase tracking-wider text-slate-400">
                        <div className="flex items-center gap-2 text-cyan-300">
                          <Ticket className="h-4 w-4" />
                          Récapitulatif
                        </div>
                        <p className="mt-2 leading-relaxed">
                          {experience?.title} — {MACHINES.find((m) => m.id === machine)?.label} ·{" "}
                          {date} à {slot} · {duration} min · {players} joueur(s)
                        </p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between border-t border-line pt-6">
                <button
                  data-testid="booking-back-btn"
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  disabled={step === 1}
                  className="flex items-center gap-2 font-hud text-xs uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-slate-300 disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" /> Retour
                </button>
                {step < 4 ? (
                  <button
                    data-testid="booking-next-btn"
                    onClick={() => setStep((s) => s + 1)}
                    disabled={!stepValid}
                    className="flex items-center gap-2 bg-cyan-400 px-6 py-3 font-hud text-xs font-bold uppercase tracking-[0.2em] text-void transition-all duration-300 hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    Continuer <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    data-testid="booking-submit-btn"
                    onClick={submit}
                    disabled={!stepValid || submitting}
                    className="flex items-center gap-2 bg-fuchsia-500 px-6 py-3 font-hud text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-fuchsia-400 hover:shadow-[0_0_28px_rgba(255,0,122,0.5)] disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Confirmer — {total}€
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
